import { faChevronRight, faCreditCard, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMoonpayDepositUrl } from '~/features/deposit/moonpay'
import { useCurrency } from '../hooks'
import { useDepositWithdraw } from '../hooks/useDepositWithdraw'
import { Currency } from '../types'

/**
 * The full registry, including methods that were never built.
 *
 * Nothing is removed from this list even though only a few entries are offered: `PayoutWalletBalance`
 * and `WithdrawRequest` both look currencies up here by name to render an icon for *historical*
 * records, so an entry that disappears would take the icon on any past record with it. What the user
 * can pick is decided by the `deposit` / `withdraw` flags at render time instead.
 */
export const currencies: Currency[] = [
    {
        name: 'moonpay',
        label: 'Credit / Debit Card',
        note: 'Powered by MoonPay',
        cardIcon: true,
        img: new URL('../assets/moonpay.jpeg', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #7B2BFF -42.57%, rgba(123, 43, 255, 0) 296.63%)',
        deposit: true,
        moonpay: true,
    },
    {
        name: 'usdc-spl',
        label: 'USDC',
        note: 'Solana network',
        img: new URL('../assets/usdc.png', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #2775CA -42.57%, rgba(39, 117, 202, 0) 296.63%)',
        decimals: 2,
        blockchain: 'solana',
        deposit: true,
        withdraw: true,
        deposit_type: 1,
    },
    {
        name: 'solana',
        label: 'SOL',
        note: 'Solana network',
        img: new URL('../assets/solana.png', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #4E4E4E -42.57%, rgba(109, 110, 221, 0) 296.63%)',
        disabled: true,
        deposit: true,
        deposit_type: 2,
        decimals: 4,
    },
    // Not built. Kept for icon lookup on historical records only — see the note above.
    {
        name: 'ethereum',
        img: new URL('../assets/ethereum.png', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #1E5CFF -42.57%, rgba(39, 96, 244, 0) 296.63%)',
        disabled: true,
    },
    {
        name: 'usdc-erc',
        img: new URL('../assets/usdc.png', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #2775CA -42.57%, rgba(39, 117, 202, 0) 296.63%)',
        disabled: true,
    },
    {
        name: 'bitcoin',
        img: new URL('../assets/bitcoin.png', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #F7931A -42.57%, rgba(247, 147, 26, 0) 296.63%)',
        disabled: true,
    },
    {
        name: 'aptos',
        img: new URL('../assets/aptos.png', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #65FEEA -42.57%, rgba(23, 100, 60, 0) 296.63%)',
        disabled: true,
    },
    {
        name: 'binance',
        img: new URL('../assets/binance.png', import.meta.url).toString(),
        gradient: 'linear-gradient(83deg, #F7C61A -42.57%, rgba(247, 212, 26, 0) 296.63%)',
        disabled: true,
    },
]

/** A payment method is not always a coin, so a card pays out a glyph rather than a token logo. */
export const CurrencyIcon = ({ currency, className = 'h-8 w-8' }: { currency: Currency; className?: string }) =>
    currency.cardIcon ? (
        <FontAwesomeIcon icon={faCreditCard} className={className} />
    ) : (
        <img src={currency.img} alt='' className={`${className} object-contain`} />
    )

const MethodRow = ({ currency, loading, onClick }) => (
    <button
        disabled={loading}
        onClick={onClick}
        className='col-span-2 md:col-span-4 group flex flex-row items-center gap-4 w-full min-h-[64px] p-4 border border-white/15 hover:border-white/60 disabled:cursor-wait transition-colors text-left'
        style={{ backgroundImage: currency.gradient }}
    >
        <div className='flex flex-col items-center justify-center w-10 shrink-0'>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin className='text-xl' /> : <CurrencyIcon currency={currency} />}
        </div>
        <div className='flex-1 min-w-0'>
            <div className='font-bold uppercase tracking-wide truncate'>{currency.label || currency.name}</div>
            {currency.note && <div className='text-xs text-white/60 truncate'>{currency.note}</div>}
        </div>
        <FontAwesomeIcon
            icon={faChevronRight}
            className='text-white/40 group-hover:text-white transition-colors shrink-0'
        />
    </button>
)

export const SelectCurrency = () => {
    const { setCurrency } = useCurrency()
    const { action } = useDepositWithdraw()
    const { isLoading: moonpayLoading } = useMoonpayDepositUrl()

    const available = currencies.filter((currency) => (action === 'withdraw' ? currency.withdraw : currency.deposit))

    return (
        <>
            <div className='col-span-2 md:col-span-4 text-xs uppercase tracking-[0.2em] text-white/50 pt-2'>
                {action === 'withdraw' ? 'Withdraw to' : 'Choose how to add funds'}
            </div>
            {available.map((currency) => (
                <MethodRow
                    key={currency.name}
                    currency={currency}
                    loading={currency.moonpay && action === 'deposit' && moonpayLoading}
                    onClick={() => setCurrency(currency)}
                />
            ))}
        </>
    )
}
